/**
 * File upload utilities
 */

/**
 * Supported image file types for uploads
 */
export const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];

/**
 * Maximum file size for uploads (5MB)
 */
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Validates uploaded file type and size
 * @param file - File to validate
 * @param maxSize - Maximum file size in bytes (default: 5MB)
 * @param allowedTypes - Array of allowed MIME types
 * @returns Object with validation result and error message if any
 */
export const validateFile = (
  file: File,
  maxSize: number = MAX_FILE_SIZE,
  allowedTypes: string[] = SUPPORTED_IMAGE_TYPES
): { isValid: boolean; error?: string } => {
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'Please upload a valid image file (JPG, PNG, JPEG, SVG)'
    };
  }
  
  if (file.size > maxSize) {
    const sizeMB = (maxSize / 1024 / 1024).toFixed(0);
    return {
      isValid: false,
      error: `File size must be less than ${sizeMB}MB`
    };
  }
  
  return { isValid: true };
};

/**
 * Formats file size to human readable format
 * @param bytes - File size in bytes
 * @returns Formatted file size string
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};
